/**
 * useDocumentViewer — shared worker-document viewing behaviour (2026-06-16).
 *
 * One contract for every surface that opens a worker document (admin
 * WorkerDocumentsDialog, the document-type drill, the worker portal profile):
 *   - images  → in-app lightbox modal
 *   - PDFs    → new tab (browser views inline)
 *   - other   → new tab (browser downloads per Content-Type)
 *
 * `file_url` MUST be a presigned (or otherwise served) URL — the uploads
 * bucket is private, so the backend mints short-lived GET URLs on read.
 *
 * Usage:
 *   const { openDocument, viewer } = useDocumentViewer();
 *   <Button onClick={() => openDocument(d.file_url, d.file_name)}>…</Button>
 *   {viewer}
 */
import React, { useCallback, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { useTranslation } from 'react-i18next';

/** Image extensions previewed inline; everything else opens in a new tab. */
export function isImageFile(name?: string | null): boolean {
  return !!name && /\.(jpe?g|png|gif|webp|bmp|heic|heif|svg)$/i.test(name);
}

interface Preview {
  url: string;
  name: string;
}

export function useDocumentViewer(): {
  openDocument: (fileUrl?: string | null, fileName?: string | null) => void;
  viewer: React.ReactNode;
} {
  const { t } = useTranslation();
  const [preview, setPreview] = useState<Preview | null>(null);

  const openDocument = useCallback(
    (fileUrl?: string | null, fileName?: string | null) => {
      if (!fileUrl) return;
      if (isImageFile(fileName)) {
        setPreview({ url: fileUrl, name: fileName ?? '' });
      } else {
        window.open(fileUrl, '_blank', 'noopener,noreferrer');
      }
    },
    [],
  );

  const viewer = (
    <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
          {preview?.name}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', justifyContent: 'center', bgcolor: 'grey.50' }}>
        {preview?.url && (
          <Box
            component="img"
            src={preview.url}
            alt={preview.name}
            sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 1 }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ py: '12px' }}>
        {preview?.url && (
          <Button
            component="a"
            href={preview.url}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewOutlinedIcon sx={{ fontSize: 14 }} />}
            sx={{ textTransform: 'none', borderRadius: 1 }}
          >
            {t('common:actions.openInNewTab')}
          </Button>
        )}
        <Button onClick={() => setPreview(null)} color="inherit">
          {t('common:actions.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return { openDocument, viewer };
}
